import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ShieldCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useCustomer } from '../lib/customerContext';
import { customerAuth } from '../lib/api';

const formatErr = (detail, fallback = 'Something went wrong') => {
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
};

export const VerifyEmailDialog = ({ open, onOpenChange, onVerified }) => {
  const { customer, verifyEmailConfirm } = useCustomer();
  const [busy, setBusy] = useState(false);
  const [otp, setOtp] = useState('');
  const sentRef = useRef(false);

  const sendCode = async (silent = false) => {
    setBusy(true);
    try {
      await customerAuth.verifyEmailSend();
      if (!silent) toast.success('Verification code sent to your email');
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail, 'Could not send code'));
    } finally { setBusy(false); }
  };

  // Auto-send the OTP once when the dialog opens
  useEffect(() => {
    if (open && !sentRef.current) {
      sentRef.current = true;
      sendCode(true);
    }
    if (!open) { sentRef.current = false; setOtp(''); }
  }, [open]);

  const confirm = async () => {
    if (!otp || otp.length < 4) { toast.error('Enter the 6-digit code from your email'); return; }
    setBusy(true);
    try {
      await verifyEmailConfirm(otp);
      toast.success('Email verified! You can place your order now.');
      onOpenChange(false);
      if (onVerified) onVerified();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail, 'Verification failed'));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="verify-email-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-orange-500" /> Verify your email
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <p className="text-[13px] text-neutral-700">
            Order place karne se pehle apna email verify karein. Humne <b>{customer?.email}</b> par ek 6-digit code bheja hai (spam folder bhi check karein).
          </p>
          <div>
            <Label className="text-[12px] text-neutral-600">Verification code</Label>
            <Input
              data-testid="verify-email-otp-input"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="6-digit code"
              inputMode="numeric"
              className="mt-1 tracking-[6px] text-center text-lg"
            />
          </div>
          <Button data-testid="verify-email-confirm-button" onClick={confirm} disabled={busy} className="w-full bg-orange-500 hover:bg-orange-600">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify email'}
          </Button>
          <div className="text-center">
            <button data-testid="verify-email-resend-button" className="text-[11px] text-orange-600 font-medium hover:underline" onClick={() => sendCode(false)} disabled={busy}>
              Resend code
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VerifyEmailDialog;
